/* eslint-disable no-param-reassign */
import Vue from 'vue'
import { getDeviceInfo } from '@/helpers/getDeviceInfo'
import { checkTokenExpiry, checkTokenValidity } from '@/helpers/token'
import jwtDefaultConfig from './jwtDefaultConfig'

// Import store directly without creating a cycle
let store = null
let router = null

const PUBLIC_AUTH_ENDPOINTS = Object.freeze([
  '/base/api/Authentication/v1/Authenticate',
  jwtDefaultConfig.loginEndpoint,
  jwtDefaultConfig.refreshEndpoint,
  '/base/api/Authentication/v1/ValidateResetPasswordToken',
  '/base/api/Authentication/v1/ValidateAndSendToken',
  '/base/api/Authentication/v1/ResendToken',
  '/base/api/Authentication/v1/ResendResetPasswordToken',
])

export const setStoreAndRouter = (storeInstance, routerInstance) => {
  store = storeInstance
  router = routerInstance
}

export default class JwtService {
  // Will be used by this service for making API calls
  axiosIns = null

  // jwtConfig <= Will be used by this service
  jwtConfig = { ...jwtDefaultConfig }

  // For Refreshing Token
  isAlreadyFetchingAccessToken = false

  // For Refreshing Token
  subscribers = []

  static isPublicAuthRequest(url = '') {
    return PUBLIC_AUTH_ENDPOINTS.some(endpoint => url.includes(endpoint))
  }

  handleSessionExpiry(message = 'Please log in again to continue. Your current session has expired due to Inactivity.') {
    this.setToken('')
    this.setRefreshToken('')
    this.setEmail('')
    this.setPermission('')

    if (store) {
      store.dispatch('auth/updateSessionState', true)
      store.dispatch('auth/updateMessage', message)
      store.dispatch('auth/logout')
    }

    if (router) {
      router.push({ name: 'auth-login' })
    }
  }

  constructor(axiosIns, jwtOverrideConfig) {
    this.axiosIns = axiosIns
    this.jwtConfig = { ...this.jwtConfig, ...jwtOverrideConfig }

    // Get User Device Information
    getDeviceInfo().then(result => {
      const previousDeviceInfo = JSON.parse(localStorage.getItem('deviceInfo'))

      // Check if there are changes
      if (JSON.stringify(result) !== JSON.stringify(previousDeviceInfo)) {
        localStorage.setItem('deviceInfo', JSON.stringify(result))
      }
    }).catch(error => {
      console.log(error)
    })

    // Request Interceptor
    this.axiosIns.interceptors.request.use(
      config => {
        // Get token from localStorage
        const accessToken = this.getToken()
        const refreshToken = this.getRefreshToken()
        const originalRequest = config
        const requestUrl = config.url || ''

        // Public auth endpoints must not be blocked by refresh-token interception.
        // This prevents deadlocks when user tries to login with stale tokens in storage.
        const isPublicAuthRequest = JwtService.isPublicAuthRequest(requestUrl)

        // Set IP Address & Device Name from localStorage
        const deviceInfo = JSON.parse(localStorage.getItem('deviceInfo') || '{}')
        const eoiEmail = localStorage.getItem('EOI-email')
        config.headers['Device-Ip-Address'] = deviceInfo.ipAddress || ''
        config.headers['Device-Type'] = deviceInfo.deviceType || ''
        if (eoiEmail && config.url === '/accounting/api/PartnerFinancialInstitution/v1/PFISubmit') {
          config.headers['EOI-email'] = eoiEmail
        }

        // Get current route if router is available
        if (router && router.currentRoute) {
          const { currentRoute } = router
          config.headers['source-route'] = currentRoute.path || ''
        }

        // Get language from localStorage
        const lang = localStorage.getItem('language') || 'en'
        const language = (lang === 'en') ? 'en-GB' : 'fr-FR'

        // Set the Accept-Language header
        config.headers['Accept-Language'] = language

        if (isPublicAuthRequest) {
          // Auth routes should never carry stale auth headers, but still need base metadata headers.
          delete config.headers.Authorization
          delete config.headers.accesstoken
          config.headers.usercomponent = `''|''|${window.location.href}`
          return config
        }

        // If token is present add it to request's Authorization Header
        if (accessToken) {
          // check if token has not expired
          if (checkTokenExpiry(accessToken)) {
            // check if refresh token has not expired
            if (!checkTokenExpiry(refreshToken)) {
              // get refresh token
              if (!this.isAlreadyFetchingAccessToken) {
                this.isAlreadyFetchingAccessToken = true
                this.refreshToken().then(r => {
                  this.isAlreadyFetchingAccessToken = false

                  // Update accessToken in localStorage
                  this.setToken(r.data.accessToken)
                  this.setRefreshToken(r.data.refreshToken)

                  this.onAccessTokenFetched(r.data.accessToken)
                }).catch(() => {
                  this.isAlreadyFetchingAccessToken = false
                  this.onAccessTokenFetched(null)
                  this.handleSessionExpiry()
                })
              }
              const retryOriginalRequest = new Promise((resolve, reject) => {
                this.addSubscriber(token => {
                  if (!token) {
                    reject(new Error('Session refresh failed. Please log in again.'))
                    return false
                  }
                  // Change Authorization header
                  originalRequest.headers.Authorization = `${this.jwtConfig.tokenType} ${token}`
                  resolve(this.axiosIns(originalRequest))
                  return false
                })
              })
              return retryOriginalRequest
            }
            // log user out
            this.handleSessionExpiry()
          } else {
            const permission = this.getUserPermission()
            config.headers.Authorization = `${this.jwtConfig.tokenType} ${accessToken}`
            config.headers.accesstoken = accessToken
            config.headers.usercomponent = this.encryptHeader(permission)
          }
        }
        if (!accessToken) {
          config.headers.usercomponent = `''|''|${window.location.href}`
        }
        return config
      },
      error => Promise.reject(error),
    )

    // Add request/response interceptor
    this.axiosIns.interceptors.response.use(response => {
      if (response && response.status === 202 && router) {
        const { currentRoute } = router
        const excludedRoutes = ['pfi-onboarding-add', 'pfi-onboarding-edit']

        if (!excludedRoutes.includes(currentRoute.name)) {
          Vue.swal({
            text: response.data.Message,
            icon: 'success',
            customClass: {
              confirmButton: 'btn btn-primary',
            },
          })
          if (response.data.IsLMSUser) {
            router.push({ name: 'lms-approvals-request' })
          } else {
            router.push({ name: 'approvals-request' })
          }
        }
      }
      return response
    }, error => {
      const { response } = error

      if (store && router) {
        if ((response && response.status === 401)) {
          console.log('error status', response.status)
        }
        if ((response && response.status === 417)) {
          store.dispatch('auth/updateSessionState', true)
          store.dispatch('auth/updateMessage', 'You were logged out either because your session had expired, or your password was changed. Kindly login again to continue')
          router.push({ name: 'auth-login' })
        }
        if ((response && response.status === 419)) {
          store.dispatch('auth/updateSessionState', true)
          store.dispatch('auth/updateMessage', 'You were logged out because the system detected your profile was active in more than one session. If this wasn’t you, please change your password immediately')
          router.push({ name: 'auth-login' })
        }
      }
      return Promise.reject(error)
    })
  }

  onAccessTokenFetched(accessToken) {
    this.subscribers = this.subscribers.filter(callback => callback(accessToken))
  }

  addSubscriber(callback) {
    this.subscribers.push(callback)
  }

  getToken() {
    const token = localStorage.getItem(this.jwtConfig.storageTokenKeyName)
    // Return null if token is 'null', 'undefined', empty string, or actually null/undefined
    if (!token || token === 'null' || token === 'undefined') {
      return null
    }
    return token
  }

  getRefreshToken() {
    const token = localStorage.getItem(this.jwtConfig.storageRefreshTokenKeyName)
    // Return null if token is 'null', 'undefined', empty string, or actually null/undefined
    if (!token || token === 'null' || token === 'undefined') {
      return null
    }
    return token
  }

  getEmail() {
    const email = localStorage.getItem(this.jwtConfig.storageUserEmail)
    // Return null if email is 'null', 'undefined', empty string, or actually null/undefined
    if (!email || email === 'null' || email === 'undefined') {
      return null
    }
    return email
  }

  getUserPermission() {
    const permission = sessionStorage.getItem(this.jwtConfig.storageUserPermission)
    // Return null if permission is 'null', 'undefined', empty string, or actually null/undefined
    if (!permission || permission === 'null' || permission === 'undefined') {
      return null
    }
    return permission
  }

  setToken(value) {
    // Only set if value is valid (not null, undefined, or empty string)
    if (value && value !== 'null' && value !== 'undefined') {
      localStorage.setItem(this.jwtConfig.storageTokenKeyName, value)
    } else {
      localStorage.removeItem(this.jwtConfig.storageTokenKeyName)
    }
  }

  setRefreshToken(value) {
    // Only set if value is valid (not null, undefined, or empty string)
    if (value && value !== 'null' && value !== 'undefined') {
      localStorage.setItem(this.jwtConfig.storageRefreshTokenKeyName, value)
    } else {
      localStorage.removeItem(this.jwtConfig.storageRefreshTokenKeyName)
    }
  }

  setEmail(value) {
    // Only set if value is valid (not null, undefined, or empty string)
    if (value && value !== 'null' && value !== 'undefined') {
      localStorage.setItem(this.jwtConfig.storageUserEmail, value)
    } else {
      localStorage.removeItem(this.jwtConfig.storageUserEmail)
    }
  }

  setPermission(value) {
    // Only set if value is valid (not null, undefined, or empty string)
    if (value && value !== 'null' && value !== 'undefined') {
      sessionStorage.setItem(this.jwtConfig.storageUserPermission, value)
    } else {
      sessionStorage.removeItem(this.jwtConfig.storageUserPermission)
    }
  }

  login(...args) {
    return this.axiosIns.post(this.jwtConfig.loginEndpoint, ...args)
  }

  register(...args) {
    return this.axiosIns.post(this.jwtConfig.registerEndpoint, ...args)
  }

  refreshToken() {
    return this.axiosIns.post(this.jwtConfig.refreshEndpoint, {
      refreshToken: this.getRefreshToken(),
    })
  }

  encryptHeader(permission) {
    const url = window.location.href
    const userName = this.getEmail()
    return `${userName}|${permission}|${url}`
  }

  checkTokenExpiration() {
    const token = this.getToken()
    if (token && checkTokenValidity(token)) {
      return false
    }
    return true
  }

  checkRefreshTokenExpiration() {
    const refreshToken = this.getRefreshToken()
    if (refreshToken && checkTokenValidity(refreshToken)) {
      return false
    }
    return true
  }
}
